import React, { useState, useEffect, useCallback } from 'react';
import { useBioData } from '../../context/BioDataContext';
import { useSettings } from '../../context/SettingsContext';
import { calculateEstimatedSalary } from '../../utils/calculationUtils';
import { calculateImprovedAIME, calculatePIAFromAIME, getSSACalculatorLink } from '../../utils/socialSecurityUtils';

const SocialSecurityCalculator = ({ onCalculate, startYear }) => {
  const { bioData, handleBioChange, resetToAutomaticSS } = useBioData();
  const { globalSettings, handleGlobalSettingChange } = useSettings();
  const [includeSSA, setIncludeSSA] = useState(true);
  // Check if user has manual override active or if we should use automatic calculations
  const [manualEstimate, setManualEstimate] = useState(bioData.isSSManualOverride || false);
  const [showAIMEDetails, setShowAIMEDetails] = useState(false);

  const [benefitAt62, setBenefitAt62] = useState(0);
  const [benefitAt67, setBenefitAt67] = useState(0);
  const [benefitAt70, setBenefitAt70] = useState(0);
  const [aimeEstimate, setAimeEstimate] = useState(null);
  const [estimatedPIA, setEstimatedPIA] = useState(0);

  const getCurrentBenefit = useCallback(() => {
    const { ssaStartAge } = globalSettings;
    if (ssaStartAge === 62) return benefitAt62;
    if (ssaStartAge === 67) return benefitAt67;
    if (ssaStartAge === 70) return benefitAt70;
    if (ssaStartAge > 62 && ssaStartAge < 67) {
      const ratio = (ssaStartAge - 62) / (67 - 62);
      return benefitAt62 + ratio * (benefitAt67 - benefitAt62);
    } else if (ssaStartAge > 67 && ssaStartAge < 70) {
      const ratio = (ssaStartAge - 67) / (70 - 67);
      return benefitAt67 + ratio * (benefitAt70 - benefitAt67);
    }
    return benefitAt67;
  }, [globalSettings, benefitAt62, benefitAt67, benefitAt70]);

  const estimateSocialSecurityBenefit = useCallback(() => {
    // First, check if automatic calculation is already available and we should use it
    if (!manualEstimate && bioData.pia && bioData.isSSAutocalculated) {
      // Use existing automatic calculation
      setEstimatedPIA(bioData.pia);
      
      // Calculate benefits at different claiming ages from existing PIA
      setBenefitAt62(Math.round(bioData.pia * 0.70)); // 30% reduction for claiming at 62
      setBenefitAt67(Math.round(bioData.pia)); // Full benefit at FRA
      setBenefitAt70(Math.round(bioData.pia * 1.24)); // 24% increase for delaying to 70
      
      // Try to get AIME details if available
      if (bioData.dateOfBirth && bioData.entryOnDuty && bioData.currentGrade && bioData.currentStep && bioData.locality) {
        // Calculate current salary from grade/step/locality
        const currentSalary = calculateEstimatedSalary(bioData.currentGrade, bioData.currentStep, bioData.locality);
        const bioDataWithSalary = { ...bioData, currentSalary };
        
        const aimeResult = calculateImprovedAIME(bioDataWithSalary);
        if (aimeResult) {
          setAimeEstimate(aimeResult);
        }
      }
      return;
    }

    // Manual calculation or no automatic calculation available
    if (!manualEstimate && bioData.dateOfBirth && bioData.entryOnDuty && bioData.currentGrade && bioData.currentStep && bioData.locality) {
      // Calculate current salary and use improved AIME calculation
      const currentSalary = calculateEstimatedSalary(bioData.currentGrade, bioData.currentStep, bioData.locality);
      const bioDataWithSalary = { ...bioData, currentSalary };
      const aimeResult = calculateImprovedAIME(bioDataWithSalary);
      
      if (aimeResult) {
        setAimeEstimate(aimeResult);
        const pia = calculatePIAFromAIME(aimeResult.aime);
        setEstimatedPIA(pia);
        
        // Calculate benefits at different claiming ages  
        setBenefitAt62(Math.round(pia * 0.70)); // 30% reduction for claiming at 62
        setBenefitAt67(Math.round(pia)); // Full benefit at FRA
        setBenefitAt70(Math.round(pia * 1.24)); // 24% increase for delaying to 70
        
        // Store PIA in bioData for other calculations (this will set manual override flag)
        handleBioChange('pia', pia);
        handleBioChange('ssRetirementAge', globalSettings.ssaStartAge);
        return;
      }
    }
    
    // Fallback to simple calculation if AIME calculation fails or manual mode
    let estimatedCurrentSalary = 50000;
    if (bioData && bioData.currentGrade && bioData.currentStep && bioData.locality) {
        estimatedCurrentSalary = calculateEstimatedSalary(
            bioData.currentGrade, bioData.currentStep, bioData.locality
        );
    }
    let primaryInsuranceAmountAnnual = 0;
    const bendPoint1Monthly = 1174;
    const bendPoint2Monthly = 7078;
    const estimatedMonthlySalary = estimatedCurrentSalary / 12;

    if (estimatedMonthlySalary <= bendPoint1Monthly) {
        primaryInsuranceAmountAnnual = (estimatedMonthlySalary * 0.90) * 12;
    } else if (estimatedMonthlySalary <= bendPoint2Monthly) {
        primaryInsuranceAmountAnnual = ((bendPoint1Monthly * 0.90) + (estimatedMonthlySalary - bendPoint1Monthly) * 0.32) * 12;
    } else {
        primaryInsuranceAmountAnnual = ((bendPoint1Monthly * 0.90) + (bendPoint2Monthly - bendPoint1Monthly) * 0.32 + (estimatedMonthlySalary - bendPoint2Monthly) * 0.15) * 12;
    }
    const maxAnnualBenefitFRA = 3822 * 12;
    primaryInsuranceAmountAnnual = Math.min(primaryInsuranceAmountAnnual, maxAnnualBenefitFRA);
    setEstimatedPIA(primaryInsuranceAmountAnnual);
    
    setBenefitAt67(Math.round(primaryInsuranceAmountAnnual));
    setBenefitAt62(Math.round(primaryInsuranceAmountAnnual * 0.70));
    setBenefitAt70(Math.round(primaryInsuranceAmountAnnual * 1.24));
  }, [bioData, globalSettings, manualEstimate, handleBioChange]);

  useEffect(() => { if (!manualEstimate) { estimateSocialSecurityBenefit(); } }, [manualEstimate, estimateSocialSecurityBenefit]);

  useEffect(() => {
    if (!includeSSA || !bioData.dateOfBirth) { 
      onCalculate({ benefits: [], rawBenefitAt62: 0 }); 
      // Clear PIA when SS is not included
      handleBioChange('pia', null);
      handleBioChange('ssRetirementAge', null);
      return; 
    }
    const birthYear = new Date(bioData.dateOfBirth).getFullYear();
    const ssaBenefitStartYear = birthYear + globalSettings.ssaStartAge;
    const annualBenefitAtClaimAge = getCurrentBenefit();
    const benefits = [];
    const endCalcYear = globalSettings.endPensionYear > startYear ? globalSettings.endPensionYear : startYear + 40;
    const ssaColaRate = globalSettings.annualCola / 100;
    for (let currentCalcYear = startYear; currentCalcYear <= endCalcYear; currentCalcYear++) {
      if (currentCalcYear >= ssaBenefitStartYear) {
        const yearsSinceStart = Math.max(0, currentCalcYear - ssaBenefitStartYear);
        const inflatedBenefit = annualBenefitAtClaimAge * Math.pow(1 + ssaColaRate, yearsSinceStart);
        benefits.push(Math.round(inflatedBenefit));
      } else { benefits.push(0); }
    }
    onCalculate({ benefits: benefits, rawBenefitAt62: benefitAt62 }); 
  }, [includeSSA, globalSettings.ssaStartAge, benefitAt62, benefitAt67, benefitAt70, bioData.dateOfBirth, globalSettings.annualCola, globalSettings.endPensionYear, startYear, onCalculate, getCurrentBenefit, handleBioChange]);

  const handleManualChange = (setter, value) => { setter(parseInt(value || 0) * 12); };

  return (
    <div className="calculator-form">
      <div className="form-group toggle-group">
        <label htmlFor="include-ssa-toggle">
          <input
            type="checkbox"
            id="include-ssa-toggle"
            checked={includeSSA}
            onChange={(e) => setIncludeSSA(e.target.checked)}
          />
          <span>Include Social Security:</span>
        </label>
      </div>

      {includeSSA && (
        <>
          <div className="info-box">
            <p>
              <strong>Important:</strong> The automatic estimates below are highly simplified. For a personalized estimate, please use the tools on the{' '}
              <a href="https://www.ssa.gov/myaccount/" target="_blank" rel="noopener noreferrer" title="Visit the Social Security Administration website">
                 SSA website
              </a>. Use "Custom Estimates" below to enter figures from your statement.
            </p>
          </div>

          <div className="form-row">
            <div className="form-group compact">
              <label htmlFor="ssa-start-age">Benefit Start Age:
                <div className="tooltip" style={{display: 'inline-block', marginLeft: '5px'}}>
                  <span className="tooltip-icon">?</span>
                  <span className="tooltip-text">Select the age you plan to start receiving benefits. Claiming earlier than Full Retirement Age (FRA) reduces amounts; delaying past FRA (up to 70) increases them.</span>
                </div>
              </label>
              <select id="ssa-start-age" value={globalSettings.ssaStartAge} onChange={(e) => handleGlobalSettingChange('ssaStartAge', parseInt(e.target.value))} className="select-input">
                {Array.from({ length: 9 }, (_, i) => i + 62).map(age => {
                  return <option key={age} value={age}>{age}</option>;
                })}
              </select>
            </div>

            <div className="form-group toggle-group">
              <label htmlFor="manual-ssa-toggle">
                <input type="checkbox" id="manual-ssa-toggle" checked={manualEstimate} onChange={(e) => {
                  setManualEstimate(e.target.checked);
                  if (!e.target.checked && bioData.isSSManualOverride) {
                    // Reset to automatic if switching from manual
                    resetToAutomaticSS();
                  }
                }} /> <span>Use Custom Estimates:</span>
              </label>
              <span className="help-text">
                {bioData.isSSAutocalculated && !manualEstimate 
                  ? "(Using automatic calculation based on your federal career)" 
                  : "(Enter amounts from SSA statement)"
                }
              </span>
            </div>
          </div>

          <div className="benefit-inputs">
             <div className="form-group compact">
               <label htmlFor="benefit-62">Est. Monthly Benefit at Age 62:
                 <div className="tooltip" style={{display: 'inline-block', marginLeft: '5px'}}>
                   <span className="tooltip-icon">?</span>
                   <span className="tooltip-text">Benefit if starting at earliest age (reduced).</span>
                 </div>
               </label>
                <div className="input-with-prefix">
                   <span className="input-prefix">$</span>
                   <input type="number" id="benefit-62" value={Math.round(benefitAt62 / 12)} onChange={(e) => handleManualChange(setBenefitAt62, e.target.value)} min={0} step={10} disabled={!manualEstimate} className={!manualEstimate ? "calculated-field" : ""} aria-label="Estimated Monthly Social Security Benefit at Age 62" placeholder="e.g., 1800" />
                   <span className="input-suffix">/ month</span>
                </div>
             </div>
             <div className="form-group compact">
               <label htmlFor="benefit-67">Est. Monthly Benefit at Age 67 (FRA):
                 <div className="tooltip" style={{display: 'inline-block', marginLeft: '5px'}}>
                   <span className="tooltip-icon">?</span>
                   <span className="tooltip-text">Benefit if starting at Full Retirement Age (typically 67 for born 1960+).</span>
                 </div>
               </label>
                <div className="input-with-prefix">
                   <span className="input-prefix">$</span>
                   <input type="number" id="benefit-67" value={Math.round(benefitAt67 / 12)} onChange={(e) => handleManualChange(setBenefitAt67, e.target.value)} min={0} step={10} disabled={!manualEstimate} className={!manualEstimate ? "calculated-field" : ""} aria-label="Estimated Monthly Social Security Benefit at Full Retirement Age (67)" placeholder="e.g., 2800" />
                   <span className="input-suffix">/ month</span>
                </div>
             </div>
             <div className="form-group compact">
               <label htmlFor="benefit-70">Est. Monthly Benefit at Age 70:
                 <div className="tooltip" style={{display: 'inline-block', marginLeft: '5px'}}>
                   <span className="tooltip-icon">?</span>
                   <span className="tooltip-text">Maximum possible benefit if delaying until age 70.</span>
                 </div>
               </label>
                <div className="input-with-prefix">
                   <span className="input-prefix">$</span>
                   <input type="number" id="benefit-70" value={Math.round(benefitAt70 / 12)} onChange={(e) => handleManualChange(setBenefitAt70, e.target.value)} min={0} step={10} disabled={!manualEstimate} className={!manualEstimate ? "calculated-field" : ""} aria-label="Estimated Monthly Social Security Benefit at Age 70" placeholder="e.g., 3500" />
                   <span className="input-suffix">/ month</span>
                </div>
             </div>
          </div>

          <div className="benefit-summary">
            <p>
              <strong>Selected Benefit Start: Age {globalSettings.ssaStartAge}</strong><br/>
              Estimated Initial Annual Benefit:{' '}<strong>${Math.round(getCurrentBenefit()).toLocaleString()}</strong>
              {' '} (~${Math.round(getCurrentBenefit() / 12).toLocaleString()} / month)
              <span className="help-text" style={{display: 'block', marginTop: '4px'}}>Before potential future COLAs.</span>
            </p>
          </div>

          {/* AIME Calculation Details */}
          {aimeEstimate && !manualEstimate && (
            <div className="aime-details" style={{marginTop: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <h4 style={{margin: '0 0 10px 0', color: '#2c3e50'}}>
                  Calculation Details
                  {bioData.isSSAutocalculated && (
                    <span style={{marginLeft: '10px', fontSize: '12px', color: '#27ae60', fontWeight: 'normal'}}>
                      ✓ Auto-calculated
                    </span>
                  )}
                </h4>
                <button 
                  type="button"
                  onClick={() => setShowAIMEDetails(!showAIMEDetails)}
                  style={{
                    background: 'none', 
                    border: '1px solid #3498db', 
                    color: '#3498db', 
                    padding: '4px 8px', 
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  {showAIMEDetails ? 'Hide Details' : 'Show Details'}
                </button>
              </div>
              
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px'}}>
                <div>
                  <strong>AIME</strong><br/>
                  <span>${aimeEstimate.aime?.toLocaleString() || '0'}/month</span><br/>
                  <small style={{color: '#666'}}>Average Indexed Monthly Earnings</small>
                </div>
                <div>
                  <strong>PIA</strong><br/>
                  <span>${Math.round(estimatedPIA / 12)?.toLocaleString() || '0'}/month</span><br/>
                  <small style={{color: '#666'}}>Primary Insurance Amount</small>
                </div>
                <div>
                  <strong>Career Estimate</strong><br/>
                  <span>Started GS-{aimeEstimate.startGradeEstimate}</span><br/>
                  <small style={{color: '#666'}}>{aimeEstimate.progressionType} progression</small>
                </div>
                <div>
                  <strong>Confidence</strong><br/>
                  <span style={{color: aimeEstimate.confidence === 'High' ? '#27ae60' : aimeEstimate.confidence === 'Medium' ? '#f39c12' : '#e74c3c'}}>
                    {aimeEstimate.confidence}
                  </span><br/>
                  <small style={{color: '#666'}}>Estimate accuracy</small>
                </div>
              </div>

              {showAIMEDetails && (
                <div style={{marginTop: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '3px'}}>
                  <h5 style={{margin: '0 0 10px 0'}}>Earnings History Summary</h5>
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', fontSize: '14px'}}>
                    <div>
                      <strong>Federal Career:</strong><br/>
                      {aimeEstimate.federalYears} years<br/>
                      <small>GS-{aimeEstimate.startGradeEstimate} to GS-{bioData.currentGrade}</small>
                    </div>
                    <div>
                      <strong>Pre-Federal:</strong><br/>
                      {aimeEstimate.preFederalYears} years<br/>
                      <small>Estimated earnings</small>
                    </div>
                    <div>
                      <strong>Total Years:</strong><br/>
                      {aimeEstimate.totalYears} years<br/>
                      <small>Highest 35 used</small>
                    </div>
                  </div>
                </div>
              )}

              <div style={{marginTop: '15px', padding: '10px', backgroundColor: '#e8f4fd', borderLeft: '3px solid #3498db'}}>
                <strong>Want a precise calculation?</strong><br/>
                <span style={{fontSize: '14px'}}>
                  For the most accurate estimate, use the official SSA calculator with your actual earnings history:{' '}
                  <a 
                    href={getSSACalculatorLink().url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{color: '#3498db', textDecoration: 'underline'}}
                  >
                    SSA.gov AIME Calculator
                  </a>
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SocialSecurityCalculator;